import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import {
  RefreshCw, Plus, Send, ShieldCheck, KeyRound, Ban, Copy, EyeOff, Eye as EyeIcon, MessageSquareQuote,
  Trash2, Vote, AlertTriangle,
} from "lucide-react";

/** Phase 10.4–10.6 — Admin: Candidate Q&A + verification + portal tokens.
 * Admin can NEVER edit answer text — moderation only withholds/restores with a reason. */

const QST_CLS = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  open: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed: "bg-blue-50 text-blue-800 border-blue-200",
};
const VER_CLS = {
  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
  pending_verification: "bg-amber-50 text-amber-900 border-amber-300",
  unverified: "bg-slate-50 text-slate-600 border-slate-200",
};

const CreateQuestionDialog = ({ munis, onDone }) => {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [deadline, setDeadline] = useState("");
  const [charLimit, setCharLimit] = useState("1500");
  const [muniId, setMuniId] = useState("");
  const create = async () => {
    if (!question.trim() || !deadline) { toast.error("Question text and deadline are required."); return; }
    try {
      await adminApi.post("/admin/qa/questions", {
        question: question.trim(),
        deadline: new Date(deadline).toISOString(),
        char_limit: parseInt(charLimit, 10) || 1500,
        municipality_id: muniId || null,
      });
      toast.success("Question created (draft). Open it to publish.");
      setOpen(false); setQuestion(""); setDeadline("");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create question."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="qa-create-question-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New question
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>New candidate question</DialogTitle>
          <DialogDescription>
            Every invited candidate receives the identical question, character limit and deadline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Question</Label>
            <Textarea className="mt-1" value={question} maxLength={500}
                      onChange={(e) => setQuestion(e.target.value)} data-testid="qa-new-question-text" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Answer deadline</Label>
              <Input type="datetime-local" className="mt-1" value={deadline}
                     onChange={(e) => setDeadline(e.target.value)} data-testid="qa-new-question-deadline" />
            </div>
            <div>
              <Label className="text-xs">Character limit</Label>
              <Input type="number" min={200} max={5000} className="mt-1" value={charLimit}
                     onChange={(e) => setCharLimit(e.target.value)} data-testid="qa-new-question-limit" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Municipality scope (optional)</Label>
            <Select value={muniId || undefined} onValueChange={setMuniId}>
              <SelectTrigger className="mt-1 bg-white" data-testid="qa-new-question-muni"><SelectValue placeholder="All candidates" /></SelectTrigger>
              <SelectContent className="bg-white">
                {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="bg-primary hover:bg-primary/90 w-full" onClick={create} data-testid="qa-new-question-save">
            Create draft
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ModerateButton = ({ answerRow, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const withheld = answerRow.status === "withheld";
  const act = async () => {
    try {
      await adminApi.patch(`/admin/qa/answers/${answerRow.answer_id}/moderate`, {
        withheld: !withheld, reason: reason.trim() || null,
      });
      toast.success(withheld ? "Answer restored." : "Answer withheld.");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Moderation failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`qa-moderate-${answerRow.candidate.slug}`}>
          {withheld ? <EyeIcon className="h-3.5 w-3.5 mr-1" /> : <EyeOff className="h-3.5 w-3.5 mr-1" />}
          {withheld ? "Restore" : "Withhold"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>{withheld ? "Restore answer" : "Withhold answer"}</DialogTitle>
          <DialogDescription>
            Moderation never edits answer text — it can only withhold or restore with a recorded reason.
          </DialogDescription>
        </DialogHeader>
        {!withheld && (
          <div>
            <Label className="text-xs">Reason (required, recorded in audit log)</Label>
            <Textarea className="mt-1" value={reason} maxLength={500}
                      onChange={(e) => setReason(e.target.value)} data-testid="qa-moderate-reason" />
          </div>
        )}
        <Button className="bg-primary hover:bg-primary/90 w-full" onClick={act} data-testid="qa-moderate-confirm">
          Confirm
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const VerifyDialog = ({ cand, onDone }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(cand.verification_status || "unverified");
  const [source, setSource] = useState(cand.verification_source || "");
  const [sourceUrl, setSourceUrl] = useState(cand.verification_source_url || "");
  const [website, setWebsite] = useState(cand.campaign_website || "");
  const save = async () => {
    try {
      await adminApi.patch(`/admin/qa/candidates/${cand.id}/verification`, {
        verification_status: status,
        verification_source: source.trim() || null,
        verification_source_url: sourceUrl.trim() || null,
        campaign_website: website.trim() || null,
      });
      toast.success("Verification record updated.");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`qa-verify-${cand.slug}`}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verification
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>Candidate verification — {cand.name}</DialogTitle>
          <DialogDescription>
            “Verified Candidate” requires an authoritative source (e.g., the municipality's official
            nominated-candidates list). Account creation or portal access never grants verification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 bg-white" data-testid="qa-verify-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="verified">Verified Candidate</SelectItem>
                <SelectItem value="pending_verification">Pending Verification</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Authoritative source name {status === "verified" && "(required)"}</Label>
            <Input className="mt-1" value={source} maxLength={300} placeholder="e.g., City of Kawartha Lakes — Certified List of Candidates"
                   onChange={(e) => setSource(e.target.value)} data-testid="qa-verify-source" />
          </div>
          <div>
            <Label className="text-xs">Authoritative source URL {status === "verified" && "(required)"}</Label>
            <Input className="mt-1" value={sourceUrl} maxLength={800} placeholder="https://…"
                   onChange={(e) => setSourceUrl(e.target.value)} data-testid="qa-verify-source-url" />
          </div>
          <div>
            <Label className="text-xs">Campaign website (optional)</Label>
            <Input className="mt-1" value={website} maxLength={500} placeholder="https://…"
                   onChange={(e) => setWebsite(e.target.value)} data-testid="qa-verify-website" />
          </div>
          <Button className="bg-primary hover:bg-primary/90 w-full" onClick={save} data-testid="qa-verify-save">
            Save verification record
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TokenCell = ({ cand, onDone }) => {
  const [issued, setIssued] = useState(null);
  const issue = async () => {
    try {
      const r = await adminApi.post(`/admin/qa/candidates/${cand.id}/portal-token`);
      setIssued(r.data);
      toast.success("Portal link issued — shown once. A copy was queued to the candidate (simulated email).");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue link."); }
  };
  const revoke = async () => {
    try {
      await adminApi.post(`/admin/qa/candidates/${cand.id}/portal-token/revoke`);
      toast.success("Portal link revoked.");
      setIssued(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke."); }
  };
  const copy = () => {
    navigator.clipboard.writeText(window.location.origin + issued.portal_url)
      .then(() => toast.success("Portal link copied."))
      .catch(() => toast.error("Copy failed."));
  };
  const revoked = !!cand.portal_token_revoked_at;
  const expired = cand.portal_token_expires_at && cand.portal_token_expires_at <= new Date().toISOString();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {cand.has_portal_token && !revoked && !expired && (
        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Active link</Badge>
      )}
      {revoked && <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 text-[10px]">Revoked</Badge>}
      {!revoked && expired && <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px]">Expired</Badge>}
      <Button size="sm" variant="outline" onClick={issue} data-testid={`qa-token-issue-${cand.slug}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1" /> {cand.has_portal_token ? "Reissue" : "Issue link"}
      </Button>
      {cand.has_portal_token && !revoked && (
        <Button size="sm" variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/5"
                onClick={revoke} data-testid={`qa-token-revoke-${cand.slug}`}>
          <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
        </Button>
      )}
      {issued && (
        <Button size="sm" variant="outline" onClick={copy} data-testid={`qa-token-copy-${cand.slug}`}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy link (shown once)
        </Button>
      )}
    </div>
  );
};

const PurgeDemoDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const purge = async () => {
    try {
      const r = await adminApi.post("/admin/qa/purge-demo-data", { confirm: confirmText });
      toast.success("Demo data purged. Tests, fixtures and real candidates untouched.");
      setOpen(false); setConfirmText(""); onDone();
      console.info("Purged:", r.data.removed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/5"
                data-testid="qa-purge-demo-btn">
          <Trash2 className="h-4 w-4 mr-1.5" /> Purge Q&A demo data
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" /> Purge Q&A demo data
          </DialogTitle>
          <DialogDescription>
            Removes ONLY the demo candidates (Test Candidate A/B/C), their invitations, Q&A answers,
            transparency answers, and demo-marked questions. Regression tests, fixtures, schemas and
            real candidate information are never touched. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <Label className="text-xs">Type <span className="font-mono font-bold">PURGE DEMO DATA</span> to confirm</Label>
        <Input className="mt-1 font-mono" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
               data-testid="qa-purge-confirm-input" />
        <Button className="bg-destructive hover:bg-destructive/90 text-white w-full" onClick={purge}
                disabled={confirmText !== "PURGE DEMO DATA"} data-testid="qa-purge-confirm-btn">
          Permanently remove demo records
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const TransparencyModDialog = ({ cand, onDone }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => {
    adminApi.get(`/admin/qa/candidates/${cand.id}/transparency`)
      .then((r) => setRows(r.data)).catch(() => setRows(null));
  };
  const moderate = async (answerId, withheld) => {
    const reason = withheld ? window.prompt("Moderation reason (required, audited, shown publicly):") : null;
    if (withheld && !(reason || "").trim()) { toast.error("A reason is required."); return; }
    try {
      await adminApi.patch(`/admin/qa/transparency-answers/${answerId}/moderate`,
                           { withheld, reason });
      toast.success(withheld ? "Answer withheld." : "Answer restored.");
      load(); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Moderation failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`qa-transparency-${cand.slug}`}>
          <Vote className="h-3.5 w-3.5 mr-1" /> Transparency
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Election Transparency — {cand.name}</DialogTitle>
          <DialogDescription>
            Voluntary answers, identical questions for every candidate. Moderation can only
            withhold/restore — answers are never edited or scored.
          </DialogDescription>
        </DialogHeader>
        {rows === null ? <Skeleton className="h-40" /> : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {!rows.invited && <p className="text-xs text-muted-foreground">Not yet invited (a portal link or Q&A invitation opens the set).</p>}
            {rows.items.map((it) => (
              <div key={it.key} className="border-b border-slate-100 pb-2 last:border-0">
                <p className="text-xs font-bold text-foreground">{it.title}</p>
                {it.answer_id ? (
                  <>
                    <p className="text-xs text-foreground mt-0.5">
                      <span className="font-semibold uppercase">{it.choice}</span>
                      {it.explanation ? ` — ${it.explanation}` : ""}
                    </p>
                    <Button size="sm" variant="outline" className="mt-1 h-7 text-[11px]"
                            onClick={() => moderate(it.answer_id, it.status !== "withheld")}
                            data-testid={`qa-transparency-mod-${it.key}`}>
                      {it.status === "withheld" ? "Restore" : "Withhold"}
                    </Button>
                  </>
                ) : (
                  <p className="text-[11px] text-muted-foreground mt-0.5">No answer yet</p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function QAAdmin() {
  const [overview, setOverview] = useState(null);
  const [cands, setCands] = useState(null);
  const [munis, setMunis] = useState([]);

  const load = useCallback(() => {
    adminApi.get("/admin/qa/overview").then((r) => setOverview(r.data)).catch(() => setOverview([]));
    adminApi.get("/admin/qa/candidates").then((r) => setCands(r.data)).catch(() => setCands([]));
    adminApi.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (qid, status) => {
    try {
      await adminApi.patch(`/admin/qa/questions/${qid}`, { status });
      toast.success(status === "open" ? "Question opened — now public." : `Question ${status}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const invite = async (qid) => {
    try {
      const r = await adminApi.post(`/admin/qa/questions/${qid}/invite`, {});
      toast.success(`Invited ${r.data.invited} candidate(s) — identical question, limit and deadline for all.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed."); }
  };

  return (
    <div data-testid="admin-qa-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <MessageSquareQuote className="h-6 w-6 text-accent" /> Candidate Q&A
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Equal treatment enforced: identical question, limit, deadline and display for every invited candidate.
            Q&A is fully separated from declarations, witnesses and individual poll responses.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="qa-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <PurgeDemoDialog onDone={load} />
          <CreateQuestionDialog munis={munis} onDone={load} />
        </div>
      </div>

      <Tabs defaultValue="questions">
        <TabsList>
          <TabsTrigger value="questions" data-testid="qa-tab-questions">Questions & answers</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="qa-tab-candidates">Candidates & verification</TabsTrigger>
        </TabsList>

        <TabsContent value="questions" className="mt-4 space-y-4">
          {overview === null && <Skeleton className="h-52" />}
          {overview !== null && overview.length === 0 && (
            <Card><CardContent className="p-6 text-sm text-muted-foreground" data-testid="qa-admin-empty">
              No candidate questions yet. Create one, invite candidates, then open it to publish.
            </CardContent></Card>
          )}
          {overview !== null && overview.map((q) => (
            <Card key={q.id} data-testid={`qa-admin-question-${q.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground">{q.question}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className={`${QST_CLS[q.status] || ""} text-[10px] uppercase`}>{q.status}</Badge>
                      {q.status === "draft" && q.invited === 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px] font-bold"
                               data-testid={`qa-draft-not-sent-${q.id}`}>
                          DRAFT — NOT SENT
                        </Badge>
                      )}
                      <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]">
                        {q.char_limit} chars
                      </Badge>
                      <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]">
                        {q.deadline ? `Deadline ${new Date(q.deadline).toLocaleString()}` : "No deadline set"}
                      </Badge>
                      <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]">
                        {q.answered} / {q.invited} answered
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => invite(q.id)} data-testid={`qa-invite-${q.id}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Invite candidates
                    </Button>
                    {q.status !== "open" && (
                      <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => setStatus(q.id, "open")}
                              data-testid={`qa-open-${q.id}`}>Open</Button>
                    )}
                    {q.status === "open" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(q.id, "closed")}
                              data-testid={`qa-close-${q.id}`}>Close</Button>
                    )}
                  </div>
                </div>

                {q.responses?.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-3 space-y-3">
                    {q.responses.map((row) => (
                      <div key={row.candidate.id} className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground">
                            {row.candidate.name}{" "}
                            <Badge variant="outline" className={`${VER_CLS[row.candidate.verification_status] || ""} text-[10px] ml-1`}>
                              {row.candidate.verification_status.replace("_", " ")}
                            </Badge>
                          </p>
                          {row.status === "answered" ? (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{row.answer}</p>
                          ) : row.status === "withheld" ? (
                            <p className="text-xs text-amber-800 mt-1">{row.note}</p>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">Invited — No Response Yet</p>
                          )}
                        </div>
                        {(row.status === "answered" || row.status === "withheld") && row.answer_id && (
                          <ModerateButton answerRow={row} onDone={load} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="candidates" className="mt-4">
          {cands === null && <Skeleton className="h-52" />}
          {cands !== null && (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Verification</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Portal access (independent of verification)</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cands.map((c) => (
                      <TableRow key={c.id} data-testid={`qa-cand-row-${c.slug}`}>
                        <TableCell>
                          <p className="font-semibold text-foreground text-sm">{c.name}</p>
                          <p className="text-[11px] text-muted-foreground">{c.municipality || "—"}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${VER_CLS[c.verification_status] || ""} text-[10px]`}>
                            {c.verification_status.replace("_", " ")}
                          </Badge>
                          {c.verified_at && (
                            <p className="text-[10px] text-muted-foreground mt-1">since {c.verified_at.slice(0, 10)}</p>
                          )}
                        </TableCell>
                        <TableCell className="max-w-52">
                          {c.verification_source ? (
                            <a href={c.verification_source_url} target="_blank" rel="noopener noreferrer"
                               className="text-[11px] text-primary underline underline-offset-2 line-clamp-2">
                              {c.verification_source}
                            </a>
                          ) : <span className="text-[11px] text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell><TokenCell cand={c} onDone={load} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap gap-1.5 justify-end">
                            <TransparencyModDialog cand={c} onDone={load} />
                            <VerifyDialog cand={c} onDone={load} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
